// NewProductScreen.tsx — the Add New Product (V6) single-page flow.
// Design: docs/design/design_handoff_add_new_product/ (README + newproduct-v6.jsx).
// Rendered by App.tsx when currentView === "new-product".
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from "react";
import { Tooltip, CircularProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setCurrentProject } from "@/state/project/projectSlice";
import {
    initialNewProductState,
    newProductReducer,
    isLocked,
    canCreate,
    collectStagedIds,
    contextMeshAnalysisRevisionKey,
    refreshContextMeshAnalysisRevisionKey,
    railSteps,
    applyNewProductDraftPatch,
    buildNewProductDraftSnapshot,
    buildPersistedNewProductDraftState,
    buildProductMeshSubmission,
    NFR_FIELDS,
    nfrValidationErrors,
    hydrateNewProductState,
    hydrateStateFromRefreshCore,
    isNewProductDraftDirty,
    migratePersistedDraft,
    type NewProductDraftPatch,
    type NewProductDraftSnapshot,
    type ProductKind,
    type NfrKey,
    type SourceConnection,
    type SourceId,
    type PrimaryFormFactor,
    StepId
} from "./state";
import { completeProductCreationDraft, discardProductCreationDraft, getProductCreationDraft, saveProductCreationDraft } from "./draftApi";
import { inferField, type InferableField } from "./inferApi";
import { FieldQualityNote } from "./FieldQualityNote";
import { useFieldQuality } from "./useFieldQuality";
import { attachStagedArtifacts, cleanupStagedArtifacts, type StagedAttachSources } from "@/api/stagedArtifacts";
import { startProductBuild } from "../products/buildApi";
import { requestProductContextMeshAnalysis } from "../products/api";
import fetchWithAuth from "@/api/secureFetch";
import { NPIcon, NPButton, OptionalBadge, RequiredBadge, NP_DASH } from "./atoms";
import { ExpandingField } from "./ExpandingField";
import { ListEditorCard } from "./ListEditorCard";
import { ProgressRail } from "./ProgressRail";
import { MeshCard } from "./MeshCard";
import { MeshInspector } from "./MeshInspector";
import { useDraftContextMeshAnalysis } from "./useDraftContextMeshAnalysis";
import {
    discardRefreshContextMeshPreview,
    getRefreshContextMeshAnalysis,
    saveRefreshContextMeshPreview,
    scheduleRefreshContextMeshAnalysis
} from "./refreshContextMeshApi";
import { useMeshStrengthData } from "../meshStrength/useMeshStrengthData";
import { KindPills } from "./KindPills";
import { IntegrationsBar } from "./IntegrationsBar";
import { ConnectSourceModal } from "./connect/ConnectSourceModal";
import type { ConnectSourceId } from "./connect/sourceConfigs";
import { FilesModal } from "./FilesModal";
import { RefreshFilesModal } from "./RefreshFilesModal";
import { ConfirmDialog } from "./ConfirmDialog";
import { RefreshBanner } from "./RefreshBanner";
import { loadRefreshSource, buildNewAnswers, type RefreshSource } from "../productRefresh/loader";
import { runRefreshScan } from "../productRefresh/scan";
import { commitMinorEvolution, commitRefinementOnly, writeCoreUpdates } from "../productRefresh/commit";
import { markFilesChanged, markIntegrationsChanged } from "../productRefresh/files";
import { startDrasticChange, runDrasticBuild, type DrasticStartResult } from "../productRefresh/drastic";
import type { RefreshCoreValues, RefreshVerdict } from "../productRefresh/types";
import {
    ScanProgressOverlay,
    NoChangesDialog,
    RefinementOnlyDialog,
    MinorEvolutionDialog,
    DrasticChangeDialog,
    BuildProgressOverlay
} from "../productRefresh/RefreshDialogs";
import "./tokens.css";
import { NavigationBackButton } from "../NavigationBackButton";

const INFER_TIP = "Infer this from what you've entered so far.";
const INFER_DISABLED_TIP = "Fill in the problem field first — inference drafts from it.";
const AGENT_URL = import.meta.env.VITE_AGENT_URL || "http://localhost:8000";
const refreshFilesConnection = (files: RefreshSource["files"]): SourceConnection => ({
    connected: files.length > 0,
    selection: files.map(file => ({ id: String(file.fileId), name: file.fileName })),
    syncedCount: files.length
});
const FORM_FACTOR_OPTIONS: Array<{ value: PrimaryFormFactor; label: string }> = [
    { value: "web", label: "Web" },
    { value: "mobile", label: "Mobile" },
    { value: "api", label: "API" }
];
const FORM_FACTOR_PILL_SX = {
    border: "none",
    borderRadius: "999px !important",
    padding: "5px 14px",
    fontSize: "11.5px",
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "none",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    background: "var(--bg-input)",
    color: "var(--fg3)",
    "&:hover": { background: "var(--bg-input)" },
    "&.Mui-selected": {
        background: "var(--np-brand)",
        color: "#fff",
        "&:hover": { background: "var(--np-brand-hover)" }
    }
} as const;

export function formatLocalDraftSaveTime(timestamp: string): string {
    const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
    const absoluteTimestamp = hasTimeZone ? timestamp : `${timestamp}Z`;
    return new Date(absoluteTimestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function InferButton({ onInfer, disabled, busy }: { onInfer: () => void; disabled: boolean; busy: boolean }) {
    return (
        <Tooltip title={disabled && !busy ? INFER_DISABLED_TIP : INFER_TIP} arrow>
            <span>
                <NPButton tone="soft" small disabled={disabled || busy} onClick={onInfer}>
                    {busy ? <CircularProgress size={12} sx={{ color: "var(--np-brand)" }} /> : <NPIcon name="sparkle" size={12} color="var(--np-brand)" />}
                    Infer
                </NPButton>
            </span>
        </Tooltip>
    );
}

interface NewProductScreenProps {
    onCancel: () => void;
    onCreated: () => void;
    draftRef?: React.MutableRefObject<NewProductDraftSnapshot | null>;
    /** Resume this saved draft; omit to start a fresh one. */
    draftId?: number | null;
    /** Pre-answer for the New-vs-Existing (Product Type) gate — set when the FDE
     *  navigated here with the user's intent already known. Applied only if the
     *  loaded draft has no kind yet; never overrides a resumed draft's choice. */
    initialKind?: ProductKind | null;
    /** Initial values supplied by an in-app creation entry point. Applied once
     *  to a fresh draft after it loads, and never over a resumed draft. */
    initialDraftPatch?: NewProductDraftPatch | null;
    /** "refresh" edits an existing product in place (KAN-1464); default "create". */
    mode?: "create" | "refresh";
    /** Required when mode === "refresh": the product being refreshed. */
    refreshProductId?: number;
    /** Refresh mode completion callback — wired by Task 7 (scan/commit). Unused this task. */
    onRefreshComplete?: (outcome: { kind: "updated" } | { kind: "rebuilt"; newProjectId: number }) => void;
    /** Files routed here programmatically (FDE chat drops → attach_file_to_new_product).
     *  Opens the Files modal and feeds them through the normal upload+staging path. */
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    /** Shown in refresh mode when opened from the product's Context Mesh. */
    onBackToContextMesh?: () => void;
}

export interface NewProductFormHandle {
    applyDraftPatch: (patch: NewProductDraftPatch) => { ok: true; draft: NewProductDraftSnapshot } | { ok: false; error: string };
}

export const NewProductScreen = React.forwardRef<NewProductFormHandle, NewProductScreenProps>(function NewProductScreen(
    {
        onCancel,
        onCreated,
        draftRef,
        draftId,
        initialKind,
        initialDraftPatch,
        mode = "create",
        refreshProductId,
        onRefreshComplete,
        intakeFiles,
        onIntakeConsumed,
        onBackToContextMesh
    },
    formRef
) {
    const isRefresh = mode === "refresh";
    const pendingExitActionRef = useRef<(() => void) | null>(null);
    const [state, d] = useReducer(newProductReducer, initialNewProductState);
    // Refresh mode has its own loading gate (refreshLoaded, below) and never
    // resumes a create-mode draft, so draftLoaded starts satisfied.
    const [draftLoaded, setDraftLoaded] = useState(isRefresh);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
    const [savedFingerprint, setSavedFingerprint] = useState("");
    const [persistedDraftIdentity, setPersistedDraftIdentity] = useState<{ draftId: number | null; version: number | null }>({
        draftId: draftId ?? null,
        version: null
    });
    const meshSnapshotRef = useRef<NewProductDraftSnapshot["contextMeshAnalysis"]>({
        status: "unavailable",
        score: null,
        tier: null,
        analyzedDraftVersion: null
    });
    const [openSource, setOpenSource] = useState<SourceId | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<"startOver" | "saveDraft" | "cancel" | null>(null);
    const dispatch = useAppDispatch();
    const token = useAppSelector(s => s.clientToken.token);
    const userId = useAppSelector(s => s.userProfiles.userProfile?.userid);
    const dirty = isNewProductDraftDirty(state);
    // Refresh mode bypasses the kind/scope lock rule (behavior 4) — the
    // reducer always hydrates with kind: "new" in refresh mode, so
    // isLocked(state) would already read false, but this makes the bypass
    // explicit rather than incidental.
    const locked = !isRefresh && isLocked(state);
    const lockStyle: React.CSSProperties = locked
        ? { opacity: 0.45, pointerEvents: "none", filter: "grayscale(0.3)" }
        : {};
    const [inferringFields, setInferringFields] = useState<ReadonlySet<InferableField>>(() => new Set());
    const inferRequestsRef = useRef<Map<InferableField, AbortController>>(new Map());
    const [nfrOpen, setNfrOpen] = useState(false);
    const nfrErrors = useMemo(() => nfrValidationErrors(state), [state.nfr]);
    const persistedState = useMemo(() => buildPersistedNewProductDraftState(state), [state]);
    const currentFingerprint = useMemo(() => JSON.stringify(persistedState), [persistedState]);
    const analysisRevisionKey = useMemo(() => contextMeshAnalysisRevisionKey(state), [state]);
    const refreshAnalysisRevisionKey = useMemo(() => refreshContextMeshAnalysisRevisionKey(state), [state]);
    const hasUnsavedChanges = draftLoaded && dirty && currentFingerprint !== savedFingerprint;
    // One-tracker rule (Jira XOR Azure DevOps): once one is staged/connected,
    // block staging the other up front rather than failing at build time.
    const trackerBlocked = useMemo(() => {
        const active = (c: SourceConnection) => Boolean(c.connected || c.staged?.processJobId || c.selection?.length);
        const jira = active(state.connections.jira);
        const azure = active(state.connections.azure);
        const blocked: Partial<Record<SourceId, string>> = {};
        // If BOTH are somehow active (legacy dirty draft), block neither so the
        // user can open each modal and disconnect one.
        if (jira && !azure) blocked.azure = "Jira is already connected — products sync with one tracker";
        if (azure && !jira) blocked.jira = "Azure DevOps is already connected — products sync with one tracker";
        return blocked;
    }, [state.connections.jira, state.connections.azure]);
    const latestStateRef = useRef(state);
    const initialDraftPatchAppliedRef = useRef(false);
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const savedRevisionRef = useRef<{ draftId: number; draftVersion: number; revisionKey: string } | null>(null);
    const refreshPreviewSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const refreshPreviewSavedRevisionRef = useRef<{ draftId: number; draftVersion: number; revisionKey: string } | null>(null);
    const refreshPreviewDraftIdRef = useRef<number | null>(null);
    const [refreshPreviewDraftId, setRefreshPreviewDraftId] = useState<number | null>(null);
    const [refreshPreviewHasCompletedAnalysis, setRefreshPreviewHasCompletedAnalysis] = useState(false);
    const isSubmittingRef = useRef(false);
    // The persisted draft's workproduct id: seeded when resuming, captured from
    // the first successful save otherwise. Reads happen inside the save queue so
    // a create followed by a queued save cannot double-create.
    const draftIdRef = useRef<number | null>(draftId ?? null);

    // Refresh mode (KAN-1464): the loaded product snapshot, retained for
    // diffing (dirty-vs-sourceRef CTA enablement) and for "Revert edits".
    // Task 7 wires the actual scan/commit; this task only loads + hydrates.
    const [sourceRef, setSourceRef] = useState<RefreshSource | null>(null);
    const [refreshLoaded, setRefreshLoaded] = useState(!isRefresh);
    const [refreshLoadError, setRefreshLoadError] = useState<string | null>(null);
    const [refreshFilesChanged, setRefreshFilesChanged] = useState(false);

    // Refresh mode (KAN-1464 Task 7): staged scan -> verdict -> commit flow.
    // `refreshPhase` drives which overlay/dialog is on screen; `scanResult`
    // retains the classified verdict + raw comparison_result for the active
    // dialog and the eventual commit call. Nothing here writes to the
    // product — see handleRefreshScan/handleRefreshConfirm below for the
    // staged-write invariant (no mutation before a verdict dialog confirm).
    type RefreshPhase = "idle" | "scanning" | { verdict: RefreshVerdict } | "committing" | "buildingDrastic";
    const [refreshPhase, setRefreshPhase] = useState<RefreshPhase>("idle");
    const [scanResult, setScanResult] = useState<unknown>(null);
    const [buildProgress, setBuildProgress] = useState<{ phase: string; message?: string }>({ phase: "" });

    // Normalize list values: trim and filter empties (follows isNewProductDraftDirty convention).
    const normList = (xs: string[]) => xs.map(v => v.trim()).filter(v => v !== "");

    // Refresh mode CTA (behavior 5) + exit-guard dirtiness: true when any
    // field/users/team differs from the loaded sourceRef. Declared above the
    // beforeunload effect (below) because in refresh mode `savedFingerprint`
    // stays "" and isNewProductDraftDirty(state) is structurally true right
    // after hydrate — using the create-mode `hasUnsavedChanges` fingerprint
    // logic for refresh mode would arm beforeunload/requestExit even with
    // zero edits. refreshDirtyVsSource compares against the actual loaded
    // source instead.
    const refreshCoreDirtyVsSource =
        sourceRef != null &&
        (state.fields.name !== sourceRef.core.name ||
            state.fields.problem !== sourceRef.core.problem ||
            state.fields.solution !== sourceRef.core.solution ||
            NFR_FIELDS.some(field => state.nfr[field.key] !== (sourceRef.core.nfr?.[field.key] ?? "")) ||
            JSON.stringify(normList(state.users)) !== JSON.stringify(normList(sourceRef.users)) ||
            JSON.stringify(normList(state.team)) !== JSON.stringify(normList(sourceRef.team)));
    const refreshIntegrationDirtyVsSource =
        sourceRef != null &&
        (Object.keys(state.connections) as SourceId[])
            .filter(source => source !== "files")
            .some(source => {
                const current = state.connections[source];
                const baseline = sourceRef.connections?.[source] ?? { connected: false };
                return current.connected !== baseline.connected || Boolean(current.staged?.processJobId || current.staged?.repoIds?.length);
            });
    const refreshDirtyVsSource = refreshCoreDirtyVsSource || refreshFilesChanged || refreshIntegrationDirtyVsSource;

    useEffect(() => {
        latestStateRef.current = state;
    }, [state]);

    // Product/Feature/Story hierarchy actions can open this existing form with
    // the relevant product context already filled in. Keep resumed drafts as
    // the source of truth and apply entry-point context only to a fresh form.
    useEffect(() => {
        if (isRefresh || !draftLoaded || draftId != null || initialDraftPatch == null || initialDraftPatchAppliedRef.current) return;
        initialDraftPatchAppliedRef.current = true;
        const nextState = applyNewProductDraftPatch(latestStateRef.current, initialDraftPatch);
        latestStateRef.current = nextState;
        d({ type: "applyDraftPatch", patch: initialDraftPatch });
    }, [draftId, draftLoaded, initialDraftPatch, isRefresh]);

    // FDE navigation pre-answer: apply initialKind once the draft has loaded,
    // but only while the gate is unanswered — a resumed draft's kind wins.
    useEffect(() => {
        if (isRefresh || !draftLoaded || initialKind == null) return;
        if (latestStateRef.current.kind !== null) return;
        d({ type: "setKind", kind: initialKind });
    }, [isRefresh, draftLoaded, initialKind]);

    // Chat-dropped files routed to this screen: surface the Files modal so the
    // pending intake queue starts feeding the upload flow (FilesModal pulls the
    // head of the queue itself).
    useEffect(() => {
        if (isRefresh || !draftLoaded || !intakeFiles?.length) return;
        setOpenSource("files");
    }, [isRefresh, draftLoaded, intakeFiles]);

    useImperativeHandle(
        formRef,
        () => ({
            applyDraftPatch: patch => {
                if (!draftLoaded) {
                    return { ok: false, error: "The Product Creation form is still loading." };
                }
                const previousState = latestStateRef.current;
                const nextState = applyNewProductDraftPatch(previousState, patch);
                latestStateRef.current = nextState;
                d({ type: "applyDraftPatch", patch });
                const nextFingerprint = JSON.stringify(buildPersistedNewProductDraftState(nextState));
                const analysisInputsChanged = contextMeshAnalysisRevisionKey(nextState) !== contextMeshAnalysisRevisionKey(previousState);
                const nextBaselineReady =
                    nextState.fields.name.trim().length >= 2 &&
                    nextState.fields.problem.trim().length >= 15 &&
                    nextState.fields.solution.trim().length >= 15;
                const contextMeshAnalysis = analysisInputsChanged
                    ? {
                          ...meshSnapshotRef.current,
                          status: meshSnapshotRef.current.score === null
                              ? nextBaselineReady ? "analyzing" as const : "unavailable" as const
                              : "stale" as const
                      }
                    : meshSnapshotRef.current;
                return {
                    ok: true,
                    draft: {
                        ...buildNewProductDraftSnapshot(nextState, contextMeshAnalysis),
                        hasUnsavedChanges: isNewProductDraftDirty(nextState) && nextFingerprint !== savedFingerprint
                    }
                };
            }
        }),
        [draftLoaded, savedFingerprint]
    );

    useEffect(() => {
        if (isRefresh) return; // refresh mode never resumes a create-mode draft
        if (!token) return;
        if (draftId == null) {
            setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState)));
            setDraftLoaded(true);
            return;
        }
        let cancelled = false;
        setDraftLoaded(false);
        getProductCreationDraft(token, draftId)
            .then(draft => {
                if (cancelled) return;
                if (draft?.draft_state) {
                    const migrated = migratePersistedDraft(draft.draft_state);
                    const restored = hydrateNewProductState(migrated);
                    d({ type: "hydrate", state: restored });
                    setSavedFingerprint(JSON.stringify(migrated));
                    savedRevisionRef.current = {
                        draftId: draft.draft_id,
                        draftVersion: draft.version,
                        revisionKey: contextMeshAnalysisRevisionKey(restored)
                    };
                    setPersistedDraftIdentity({ draftId: draft.draft_id, version: draft.version });
                    setLastSavedAt(draft.updated_at);
                    setSaveStatus("saved");
                } else {
                    draftIdRef.current = null;
                    savedRevisionRef.current = null;
                    setPersistedDraftIdentity({ draftId: null, version: null });
                    setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState)));
                    toast.warning("That draft no longer exists — starting a new one.");
                }
            })
            .catch(error => {
                if (cancelled) return;
                setSaveStatus("error");
                toast.error(error instanceof Error ? error.message : "Failed to load product draft");
            })
            .finally(() => {
                if (!cancelled) setDraftLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isRefresh, token, draftId]);

    // Refresh mode (KAN-1464 behavior 1): load the current product snapshot
    // and hydrate the reducer. `draftLoaded` is forced true above (via
    // refreshLoaded's initial state) but the screen's own loading gate below
    // uses refreshLoaded so the spinner shows until the fetch resolves.
    useEffect(() => {
        if (!isRefresh) return;
        if (!token || refreshProductId == null || userId == null) return;
        let cancelled = false;
        setRefreshLoaded(false);
        setRefreshLoadError(null);
        loadRefreshSource(token, refreshProductId, userId)
            .then(source => {
                if (cancelled) return;
                setSourceRef(source);
                d({
                    type: "hydrate",
                    state: hydrateStateFromRefreshCore(source.core, source.users, source.team, {
                        ...(source.connections ?? {}),
                        files: refreshFilesConnection(source.files)
                    })
                });
            })
            .catch(error => {
                if (cancelled) return;
                const message = error instanceof Error ? error.message : "Failed to load product";
                setRefreshLoadError(message);
                toast.error(message);
            })
            .finally(() => {
                if (!cancelled) setRefreshLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isRefresh, token, refreshProductId, userId]);

    const persistDraft = useCallback(
        (nextState = latestStateRef.current) => {
            if (!token) return Promise.reject(new Error("Your session hasn't finished loading"));
            const payload = buildPersistedNewProductDraftState(nextState);
            const fingerprint = JSON.stringify(payload);
            setSaveStatus("saving");
            const request = saveQueueRef.current
                .catch(() => undefined)
                .then(() => saveProductCreationDraft(token, payload, draftIdRef.current))
                .then(saved => {
                    draftIdRef.current = saved.draft_id;
                    savedRevisionRef.current = {
                        draftId: saved.draft_id,
                        draftVersion: saved.version,
                        revisionKey: contextMeshAnalysisRevisionKey(nextState)
                    };
                    setPersistedDraftIdentity({ draftId: saved.draft_id, version: saved.version });
                    setSavedFingerprint(fingerprint);
                    setLastSavedAt(saved.updated_at);
                    setSaveStatus("saved");
                    return saved;
                })
                .catch(error => {
                    setSaveStatus("error");
                    throw error;
                });
            saveQueueRef.current = request;
            return request;
        },
        [token]
    );

    const ensureDraftRevisionPersisted = useCallback(
        async (revisionKey: string, forceSave = false): Promise<{ draftId: number; draftVersion: number }> => {
            await saveQueueRef.current.catch(() => undefined);
            const saved = savedRevisionRef.current;
            if (!forceSave && saved && saved.revisionKey === revisionKey) {
                return { draftId: saved.draftId, draftVersion: saved.draftVersion };
            }
            const snapshot = latestStateRef.current;
            if (contextMeshAnalysisRevisionKey(snapshot) !== revisionKey) {
                throw new Error("The draft changed before analysis could begin.");
            }
            const persisted = await persistDraft(snapshot);
            return { draftId: persisted.draft_id, draftVersion: persisted.version };
        },
        [persistDraft]
    );

    const ensureQualityDraftPersisted = useCallback(
        () => ensureDraftRevisionPersisted(analysisRevisionKey),
        [analysisRevisionKey, ensureDraftRevisionPersisted]
    );
    // Creation-mode evaluations are attached to an exact persisted draft
    // revision. Refresh evaluations remain display-only and send raw values.
    const { quality, evaluate: evaluateQuality, invalidate: invalidateQuality } = useFieldQuality(token, {
        enabled: !locked,
        ensurePersisted: isRefresh ? undefined : ensureQualityDraftPersisted
    });
    const evaluatePersonasQuality = useCallback(() => {
        const value = latestStateRef.current.users.map(item => item.trim()).filter(Boolean).join(", ");
        evaluateQuality("personas", value);
    }, [evaluateQuality]);
    const cancelInference = useCallback((field?: InferableField) => {
        if (field) {
            const controller = inferRequestsRef.current.get(field);
            if (!controller) return;
            controller.abort();
            inferRequestsRef.current.delete(field);
            setInferringFields(current => {
                if (!current.has(field)) return current;
                const next = new Set(current);
                next.delete(field);
                return next;
            });
            return;
        }

        for (const controller of inferRequestsRef.current.values()) {
            controller.abort();
        }
        inferRequestsRef.current.clear();
        setInferringFields(new Set());
    }, []);
    useEffect(() => () => cancelInference(), [cancelInference]);
    const runInfer = useCallback(
        async (field: InferableField) => {
            if (!token) {
                toast.error("Your session hasn't finished loading — try again in a moment.");
                return;
            }
            inferRequestsRef.current.get(field)?.abort();
            const controller = new AbortController();
            inferRequestsRef.current.set(field, controller);
            setInferringFields(current => new Set(current).add(field));
            if (field === "users") invalidateQuality("personas");
            try {
                const result = await inferField(field, state, token, { signal: controller.signal });
                if (controller.signal.aborted) return;
                if (field === "solution" && result.value !== undefined) {
                    // Inference bypasses onChange, so retire the old score here.
                    invalidateQuality("solution");
                    d({ type: "setField", field: "solution", value: result.value });
                } else if (field === "users" && result.values !== undefined) {
                    d({ type: "appendListItems", list: "users", values: result.values });
                } else if (field.startsWith("nfr_") && result.value !== undefined) {
                    const key = field.slice(4) as NfrKey;
                    if (NFR_FIELDS.some(definition => definition.key === key)) {
                        d({ type: "setNfrField", field: key, value: result.value });
                    }
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    toast.error(error instanceof Error ? error.message : "Inference failed — try again.");
                }
            } finally {
                if (inferRequestsRef.current.get(field) === controller) {
                    inferRequestsRef.current.delete(field);
                    setInferringFields(current => {
                        const next = new Set(current);
                        next.delete(field);
                        return next;
                    });
                }
            }
        },
        [invalidateQuality, state, token]
    );
    const canInfer = state.fields.problem.trim() !== "";

    const persistRefreshPreview = useCallback(
        (nextState = latestStateRef.current) => {
            if (!token || refreshProductId == null) {
                return Promise.reject(new Error("Your session hasn't finished loading"));
            }
            const request = refreshPreviewSaveQueueRef.current
                .catch(() => undefined)
                .then(() => saveRefreshContextMeshPreview(token, refreshProductId, buildPersistedNewProductDraftState(nextState)))
                .then(saved => {
                    refreshPreviewDraftIdRef.current = saved.draftId;
                    refreshPreviewSavedRevisionRef.current = {
                        draftId: saved.draftId,
                        draftVersion: saved.draftVersion,
                        revisionKey: refreshContextMeshAnalysisRevisionKey(nextState)
                    };
                    setRefreshPreviewDraftId(saved.draftId);
                    return saved;
                });
            refreshPreviewSaveQueueRef.current = request;
            return request;
        },
        [refreshProductId, token]
    );

    const ensureRefreshPreviewPersisted = useCallback(
        async (revisionKey: string, forceSave = false): Promise<{ draftId: number; draftVersion: number }> => {
            await refreshPreviewSaveQueueRef.current.catch(() => undefined);
            const saved = refreshPreviewSavedRevisionRef.current;
            if (!forceSave && saved && saved.revisionKey === revisionKey) {
                return { draftId: saved.draftId, draftVersion: saved.draftVersion };
            }
            const snapshot = latestStateRef.current;
            if (refreshContextMeshAnalysisRevisionKey(snapshot) !== revisionKey) {
                throw new Error("The refresh preview changed before analysis could begin.");
            }
            return persistRefreshPreview(snapshot);
        },
        [persistRefreshPreview]
    );

    const getRefreshPreviewAnalysis = useCallback(
        (analysisToken: string, draftId: number) => {
            if (refreshProductId == null) return Promise.reject(new Error("A product is required for refresh analysis."));
            return getRefreshContextMeshAnalysis(analysisToken, refreshProductId, draftId);
        },
        [refreshProductId]
    );

    const scheduleRefreshPreviewAnalysis = useCallback(
        (analysisToken: string, draftId: number, draftVersion: number) => {
            if (refreshProductId == null) return Promise.reject(new Error("A product is required for refresh analysis."));
            return scheduleRefreshContextMeshAnalysis(analysisToken, refreshProductId, draftId, draftVersion);
        },
        [refreshProductId]
    );

    const discardRefreshPreview = useCallback(async (): Promise<void> => {
        await refreshPreviewSaveQueueRef.current.catch(() => undefined);
        if (token && refreshProductId != null && refreshPreviewDraftIdRef.current != null) {
            await discardRefreshContextMeshPreview(token, refreshProductId);
        }
        refreshPreviewDraftIdRef.current = null;
        refreshPreviewSavedRevisionRef.current = null;
        setRefreshPreviewDraftId(null);
        setRefreshPreviewHasCompletedAnalysis(false);
    }, [refreshProductId, token]);

    const canonicalBaselineReady =
        state.fields.name.trim().length >= 2 &&
        state.fields.problem.trim().length >= 15 &&
        state.fields.solution.trim().length >= 15;
    const draftMesh = useDraftContextMeshAnalysis({
        enabled: !isRefresh && draftLoaded,
        token,
        draftId: persistedDraftIdentity.draftId,
        revisionKey: analysisRevisionKey,
        baselineReady: canonicalBaselineReady,
        ensurePersisted: ensureDraftRevisionPersisted
    });
    const refreshPreviewMesh = useDraftContextMeshAnalysis({
        enabled: isRefresh && refreshLoaded && refreshDirtyVsSource,
        token,
        draftId: refreshPreviewDraftId,
        revisionKey: refreshAnalysisRevisionKey,
        baselineReady: canonicalBaselineReady,
        ensurePersisted: ensureRefreshPreviewPersisted,
        getAnalysis: getRefreshPreviewAnalysis,
        scheduleAnalysis: scheduleRefreshPreviewAnalysis
    });
    const productMesh = useMeshStrengthData(isRefresh ? refreshProductId ?? null : null);
    const completeProductMesh = productMesh.analysis?.categoryScores
        ? { ...productMesh.analysis, categoryScores: productMesh.analysis.categoryScores }
        : null;
    const meshAnalysis = isRefresh
        ? refreshDirtyVsSource && refreshPreviewHasCompletedAnalysis
            ? refreshPreviewMesh.analysis ?? completeProductMesh
            : completeProductMesh
        : draftMesh.analysis;
    const meshStatus = isRefresh
        ? refreshDirtyVsSource
            ? canonicalBaselineReady ? refreshPreviewMesh.status : "baseline_incomplete"
            : productMesh.status
        : canonicalBaselineReady ? draftMesh.status : "baseline_incomplete";
    const meshStale = isRefresh
        ? refreshDirtyVsSource
            ? meshAnalysis !== null && (refreshPreviewMesh.analysis === null || refreshPreviewMesh.isStale || refreshPreviewMesh.status !== "ready")
            : productMesh.status === "stale"
        : draftMesh.isStale || (!canonicalBaselineReady && draftMesh.analysis !== null);
    const meshError = isRefresh
        ? refreshDirtyVsSource
            ? canonicalBaselineReady ? refreshPreviewMesh.error?.message ?? null : null
            : productMesh.error?.message ?? null
        : canonicalBaselineReady ? draftMesh.error?.message ?? null : null;
    const meshUpdating = isRefresh
        ? refreshDirtyVsSource
            ? canonicalBaselineReady && (
                refreshPreviewMesh.status === "debouncing" ||
                refreshPreviewMesh.status === "pending" ||
                refreshPreviewMesh.status === "sources_pending"
            )
            : productMesh.loading ||
              productMesh.status === "analyzing" ||
              productMesh.analysis?.semanticAnalysis.status === "pending" ||
              productMesh.analysis?.categoryAnalysis?.status === "pending"
        : canonicalBaselineReady &&
          (draftMesh.status === "debouncing" || draftMesh.status === "pending" || draftMesh.status === "sources_pending");
    const retryMesh = isRefresh
        ? refreshDirtyVsSource ? refreshPreviewMesh.retry : productMesh.retry
        : draftMesh.retry;
    const fdeMeshSnapshot: NewProductDraftSnapshot["contextMeshAnalysis"] = {
        status: meshAnalysis
            ? meshUpdating || meshStale ? "stale" : "ready"
            : meshError ? "failed" : meshUpdating ? "analyzing" : "unavailable",
        score: meshAnalysis?.score ?? null,
        tier: meshAnalysis?.tier ?? null,
        analyzedDraftVersion: isRefresh
            ? refreshPreviewHasCompletedAnalysis ? refreshPreviewMesh.analyzedDraftVersion : null
            : draftMesh.analyzedDraftVersion
    };
    meshSnapshotRef.current = fdeMeshSnapshot;

    useEffect(() => {
        if (
            isRefresh &&
            refreshDirtyVsSource &&
            refreshPreviewMesh.analysis !== null &&
            refreshPreviewMesh.status === "ready" &&
            !refreshPreviewMesh.isStale
        ) {
            setRefreshPreviewHasCompletedAnalysis(true);
        }
    }, [
        isRefresh,
        refreshDirtyVsSource,
        refreshPreviewMesh.analysis,
        refreshPreviewMesh.isStale,
        refreshPreviewMesh.status
    ]);

    useEffect(() => {
        if (!isRefresh || !refreshLoaded || refreshDirtyVsSource || refreshPreviewDraftIdRef.current == null) return;
        void discardRefreshPreview().catch(error => {
            console.error("Failed to discard reverted Context Mesh preview", error);
        });
    }, [discardRefreshPreview, isRefresh, refreshDirtyVsSource, refreshLoaded]);

    useEffect(() => {
        // Refresh mode never autosaves a draft (behavior 6) — nothing is
        // written until the user confirms a scan verdict (Task 7).
        if (isRefresh || !draftLoaded || !dirty || !hasUnsavedChanges || !token) return;
        const timer = window.setTimeout(() => {
            void persistDraft(state).catch(() => undefined);
        }, 750);
        return () => window.clearTimeout(timer);
    }, [dirty, draftLoaded, hasUnsavedChanges, isRefresh, persistDraft, state, token]);

    useEffect(() => {
        if (!draftRef) return;
        draftRef.current = { ...buildNewProductDraftSnapshot(state, fdeMeshSnapshot), hasUnsavedChanges };
    }, [draftRef, fdeMeshSnapshot.analyzedDraftVersion, fdeMeshSnapshot.score, fdeMeshSnapshot.status, fdeMeshSnapshot.tier, hasUnsavedChanges, state]);

    useEffect(() => {
        // Refresh mode: savedFingerprint never advances (no autosave) and
        // isNewProductDraftDirty(state) reads true right after hydrate, so
        // hasUnsavedChanges would arm this prompt even with zero edits. Use
        // refreshDirtyVsSource (diffed against the loaded sourceRef) instead.
        const armed = isRefresh ? refreshDirtyVsSource : hasUnsavedChanges;
        if (!armed) return;
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges, isRefresh, refreshDirtyVsSource]);

    useEffect(
        () => () => {
            if (draftRef) draftRef.current = null;
        },
        [draftRef]
    );

    const steps = railSteps(state);

    const sectionRefs = useRef<Partial<Record<StepId, HTMLDivElement | null>>>({});
    const setRef = (id: StepId) => (el: HTMLDivElement | null) => {
        sectionRefs.current[id] = el;
    };
    const scrollTo = (id: StepId) => {
        if (isRefresh && id === "nfr") setNfrOpen(true);
        const target = id === "kind" || id === "scope" ? sectionRefs.current.kind : sectionRefs.current[id];
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const hasStagedSource = (connection: SourceConnection) =>
        Boolean(connection.staged?.processJobId || connection.staged?.repoIds?.length || connection.staged?.fileIds?.length);

    const buildStagedAttachSources = (stagedOnly = false): StagedAttachSources =>
        Object.fromEntries(
            (Object.entries(state.connections) as [SourceId, SourceConnection][])
                .filter(([, conn]) => conn.connected && (!stagedOnly || hasStagedSource(conn)))
                .map(([source, conn]) => {
                    if (source === "files") {
                        return [source, { fileIds: conn.staged?.fileIds ?? [] }];
                    }
                    if (conn.staged?.repoIds) {
                        return [source, { repoIds: conn.staged.repoIds, selection: conn.selection }];
                    }
                    return [source, { processJobId: conn.staged?.processJobId, selection: conn.selection }];
                })
        );

    const handleSourceComplete = (source: SourceId, connection: SourceConnection) => {
        const current = latestStateRef.current.connections[source];
        if (isRefresh && hasStagedSource(current) && token) {
            const staged = current.staged;
            cleanupStagedArtifacts(
                {
                    processJobIds: staged?.processJobId ? [staged.processJobId] : [],
                    repoIds: staged?.repoIds ?? []
                },
                token
            ).catch(error => console.error("Failed to clean up replaced staged artifacts", error));
        }
        d({
            type: "setConnection",
            source,
            connection: isRefresh ? { ...connection, existingLinkIds: current.existingLinkIds } : connection
        });
    };

    const handleSourceDisconnect = (source: SourceId) => {
        const conn = latestStateRef.current.connections[source];
        const staged = conn.staged;
        if (staged && (staged.processJobId || staged.repoIds?.length || staged.fileIds?.length) && token) {
            cleanupStagedArtifacts(
                {
                    processJobIds: staged.processJobId ? [staged.processJobId] : [],
                    repoIds: staged.repoIds ?? [],
                    fileIds: staged.fileIds ?? []
                },
                token
            ).catch(error => console.error("Failed to clean up staged artifacts", error));
        }
        if (isRefresh) {
            d({
                type: "setConnection",
                source,
                connection: {
                    connected: false,
                    existingLinkIds: conn.existingLinkIds
                }
            });
        } else {
            d({ type: "disconnectSource", source });
        }
    };

    const handleSourceAborted = (staged: { processJobId?: string; repoIds?: number[] }) => {
        if (!token) return;
        if (!staged.processJobId && !staged.repoIds?.length) return;
        cleanupStagedArtifacts(
            {
                processJobIds: staged.processJobId ? [staged.processJobId] : [],
                repoIds: staged.repoIds ?? []
            },
            token
        ).catch(error => console.error("Failed to clean up staged artifacts", error));
    };

    const handleFilesUploaded = (fileId?: number, fileName?: string) => {
        if (fileId == null) return;
        const existing = latestStateRef.current.connections.files;
        if (existing.staged?.fileIds?.includes(fileId)) return;
        const fileIds = [...(existing.staged?.fileIds ?? []), fileId];
        const selection = [...(existing.selection ?? []), { id: String(fileId), name: fileName ?? `File #${fileId}` }];
        d({
            type: "setConnection",
            source: "files",
            connection: { ...existing, connected: true, selection, staged: { ...existing.staged, fileIds } }
        });
    };

    const handleFileRemoved = (fileId: number) => {
        const existing = latestStateRef.current.connections.files;
        const fileIds = (existing.staged?.fileIds ?? []).filter(id => id !== fileId);
        const selection = (existing.selection ?? []).filter(item => item.id !== String(fileId));
        d({
            type: "setConnection",
            source: "files",
            connection: {
                ...existing,
                connected: fileIds.length > 0,
                selection,
                staged: { ...existing.staged, fileIds }
            }
        });
    };

    const handleCreate = async () => {
        if (isSubmittingRef.current) return;
        if (!canCreate(state)) return;
        if (!token) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return;
        }
        isSubmittingRef.current = true;
        try {
            try {
                await persistDraft(state);
            } catch (error) {
                toast.error(error instanceof Error ? error.message : "Save the product draft before creating it");
                return;
            }
            d({ type: "setCreating", creating: true });
            try {
                const response = await fetch(`${AGENT_URL}/api/product-meshes`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify(buildProductMeshSubmission(state))
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(result.detail || `Product creation failed (${response.status})`);
                }
                if (result.status === "push_failed" || result.project_id == null) {
                    const warning = Array.isArray(result.warnings) ? result.warnings.join(" ") : "Product creation did not complete.";
                    throw new Error(warning);
                }

                const pid = Number(result.project_id);
                if (Number.isFinite(pid)) {
                    if (state.kind === "existing" && state.scope) {
                        const constraintResponse = await fetchWithAuth(`/api/project/${pid}/creation-constraints`, token, {
                            method: "POST",
                            body: JSON.stringify({ feature_scope: state.scope })
                        });
                        if (!constraintResponse.ok) {
                            throw new Error("Product was created, but its feature-count constraint could not be registered.");
                        }
                    }
                    const stagedSources = buildStagedAttachSources();
                    if (result.processor_job_id && Object.keys(stagedSources).length > 0) {
                        try {
                            const attached = await attachStagedArtifacts(pid, stagedSources, token);
                            if (attached.context_mesh_refresh?.scheduled === false) {
                                toast.warning(
                                    `${attached.warning || "Context Mesh refresh needs attention."} Request ID: ${attached.context_mesh_refresh.request_id}`
                                );
                            }
                        } catch {
                            toast.error("Product created, but staged sources could not be linked - retry from the Product card.");
                        }
                    }
                    if (!result.processor_job_id) {
                        try {
                            await startProductBuild(pid, stagedSources, token);
                        } catch {
                            toast.error("Product created, but processing needs attention — use the Product card to retry.");
                        }
                    }
                    try {
                        await dispatch(setCurrentProject(pid)).unwrap();
                    } catch {
                        // Non-fatal: the product exists; the dashboard refresh will pick it up.
                    }
                    try {
                        const savedDraftId = draftIdRef.current;
                        if (savedDraftId != null) {
                            await completeProductCreationDraft(token, savedDraftId, pid);
                        }
                    } catch {
                        toast.warning("Product created, but the saved draft could not be marked complete.");
                    }
                }
                toast.success(`${state.fields.name.trim()} created`);
                if (draftRef) draftRef.current = null;
                onCreated();
            } catch (err) {
                toast.error(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to create product");
                d({ type: "setCreating", creating: false });
            }
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const handleSaveDraft = async () => {
        if (!dirty) return;
        try {
            await persistDraft(state);
            toast.success("Product draft saved");
            setPendingConfirm("saveDraft");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to save product draft");
        }
    };

    const performDiscardDraft = async () => {
        if (!token) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return;
        }
        const staged = collectStagedIds(state);
        if (staged.processJobIds.length || staged.repoIds.length || staged.fileIds.length) {
            try {
                await cleanupStagedArtifacts(staged, token);
            } catch (error) {
                console.error("Failed to clean up staged artifacts", error);
                toast.warning("Some staged sources could not be cleaned up, but the draft will still be discarded.");
            }
        }
        try {
            if (draftIdRef.current != null) {
                await discardProductCreationDraft(token, draftIdRef.current);
                draftIdRef.current = null;
            }
            savedRevisionRef.current = null;
            setPersistedDraftIdentity({ draftId: null, version: null });
            d({ type: "reset" });
            setSavedFingerprint(JSON.stringify(buildPersistedNewProductDraftState(initialNewProductState)));
            setLastSavedAt(null);
            setSaveStatus("idle");
            toast.success("Product draft discarded");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to discard product draft");
        }
    };

    const requestExit = (exitAction: () => void) => {
        // Refresh mode never autosaves/stages anything (behavior 6), so there
        // is nothing queued to lose silently on Cancel — but real in-progress
        // edits (dirty vs. the loaded sourceRef) can still be discarded
        // without warning, unlike create mode. Reuse the same confirm-dialog
        // guard create mode uses for unsaved work; exit immediately when
        // there's nothing to lose.
        if (isRefresh) {
            if (refreshDirtyVsSource) {
                pendingExitActionRef.current = exitAction;
                setPendingConfirm("cancel");
            } else if (refreshPreviewDraftIdRef.current == null) {
                exitAction();
            } else {
                void discardRefreshPreview()
                    .catch(error => console.error("Failed to discard Context Mesh preview", error))
                    .finally(exitAction);
            }
            return;
        }
        // Gate on form content, not hasUnsavedChanges: autosave (750ms debounce)
        // clears "unsaved" almost immediately, which would let Cancel exit a
        // non-empty draft without ever warning.
        if (dirty) {
            pendingExitActionRef.current = exitAction;
            setPendingConfirm("cancel");
        } else {
            exitAction();
        }
    };

    // Refresh mode (KAN-1464 Task 7): staged scan -> verdict -> commit flow.
    //
    // Staged-write invariant: nothing below writes to the product until the
    // user confirms a verdict dialog (handleRefreshConfirm*). handleRefreshScan
    // only calls runRefreshScan (a read-only WPP comparison job) and routes the
    // classified verdict to the matching dialog — it never calls a commit/write
    // function itself.
    const buildEditedCoreValues = (): RefreshCoreValues => {
        const source = sourceRef;
        return {
            name: state.fields.name,
            problem: state.fields.problem,
            solution: state.fields.solution,
            users: normList(state.users).join("\n"),
            team: normList(state.team).join("\n"),
            nfr: { ...state.nfr },
            // type/description have no UI in refresh mode — pass through
            // unchanged from the loaded source (UpdateProductModal.tsx's
            // formData.description is likewise the raw project_description
            // passed through untouched, never derived from problem/solution).
            type: source?.core.type ?? "",
            description: source?.core.description ?? ""
        };
    };

    const finishRefresh = async (outcome: { kind: "updated" } | { kind: "rebuilt"; newProjectId: number }) => {
        const completedProductId = outcome.kind === "rebuilt" ? outcome.newProjectId : refreshProductId;
        const completionTasks: Promise<unknown>[] = [discardRefreshPreview()];
        if (token && completedProductId != null) {
            completionTasks.push(requestProductContextMeshAnalysis(token, String(completedProductId)));
        }

        const [discardResult, analysisResult] = await Promise.allSettled(completionTasks);
        if (discardResult.status === "rejected") {
            console.error("Failed to discard completed Context Mesh preview", discardResult.reason);
        }
        if (analysisResult?.status === "rejected") {
            // The Product update is already committed. Keep completion successful;
            // existing mutation invalidations may still schedule analysis, and the
            // user can retry analysis from the Product Hub or Context Mesh Console.
            console.error("Failed to schedule final Context Mesh analysis", analysisResult.reason);
        }
        onRefreshComplete?.(outcome);
    };

    // Keep the shared Files chip synchronized with the modal after each
    // successful add/remove, matching create-mode's immediate count update.
    const handleRefreshFilesUpdated = (files: RefreshSource["files"]) => {
        setSourceRef(prev => (prev ? { ...prev, files } : prev));
        d({ type: "setConnection", source: "files", connection: refreshFilesConnection(files) });
    };

    // File writes are immediate, but derived work products are marked once
    // per modal session rather than once per file.
    const handleRefreshFilesChanged = (files: RefreshSource["files"]) => {
        handleRefreshFilesUpdated(files);
        setRefreshFilesChanged(true);
        if (!token || refreshProductId == null) return;
        markFilesChanged(token, refreshProductId).catch(error => {
            console.error("Failed to mark work products for rebuild after file changes", error);
        });
    };

    const getRefreshLinkIdsToRemove = (): number[] =>
        (Object.entries(state.connections) as [SourceId, SourceConnection][])
            .filter(
                ([source, connection]) =>
                    source !== "files" && Boolean(connection.existingLinkIds?.length) && (!connection.connected || hasStagedSource(connection))
            )
            .flatMap(([, connection]) => connection.existingLinkIds ?? []);

    const applyRefreshIntegrationChanges = async (targetProjectId: number) => {
        if (!token || !refreshIntegrationDirtyVsSource) return;
        const attached = await attachStagedArtifacts(
            targetProjectId,
            buildStagedAttachSources(true),
            token,
            getRefreshLinkIdsToRemove()
        );
        if (attached.context_mesh_refresh?.scheduled === false) {
            toast.warning(
                `${attached.warning || "Context Mesh refresh needs attention."} Request ID: ${attached.context_mesh_refresh.request_id}`
            );
        }
        await markIntegrationsChanged(token, targetProjectId);
    };

    const cleanupRefreshStagedChanges = async () => {
        if (!token) return;
        const staged = collectStagedIds(state);
        if (!staged.processJobIds.length && !staged.repoIds.length && !staged.fileIds.length) return;
        await cleanupStagedArtifacts(staged, token);
    };

    const handleRefreshScan = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) {
            toast.error("Your session hasn't finished loading — try again in a moment.");
            return;
        }
        const edited = buildEditedCoreValues();
        setRefreshPhase("scanning");
        try {
            const outcome = await runRefreshScan({
                token,
                projectId: refreshProductId,
                userId,
                originalAnswers: sourceRef.originalAnswers,
                newAnswers: buildNewAnswers(sourceRef.originalAnswers, sourceRef.nfrQuestionIds, edited),
                originalDescription: sourceRef.originalDescription,
                onPhase: (phase, message) => setBuildProgress({ phase, message })
            });
            // Product name and team (Q25) aren't part of the scan payload
            // (buildNewAnswers only covers Q20/22/23/24), so a name-only or
            // team-only edit always scans as NO_CHANGES. If we showed
            // NoChangesDialog here, the edit would never get written and the
            // CTA would just re-enable — an infinite loop, and a regression
            // vs. the old modal (which wrote the name pre-scan under every
            // verdict). Detect that case and write the core update directly
            // instead of surfacing the dialog. Truly nothing changed still
            // falls through to NoChangesDialog. Team compares semantically
            // (normList vs sourceRef.team) rather than string-vs-core.team so
            // legacy comma-separated Q25 answers don't false-positive here.
            const teamEdited = JSON.stringify(normList(state.team)) !== JSON.stringify(normList(sourceRef.team));
            const nfrEdited = NFR_FIELDS.some(
                field => edited.nfr[field.key] !== (sourceRef.core.nfr?.[field.key] ?? "")
            );
            if (
                outcome.verdict === "NO_CHANGES" &&
                (edited.name !== sourceRef.core.name ||
                    teamEdited ||
                    nfrEdited ||
                    refreshFilesChanged ||
                    refreshIntegrationDirtyVsSource)
            ) {
                if (edited.name !== sourceRef.core.name || teamEdited || nfrEdited) {
                    await writeCoreUpdates({
                        token,
                        projectId: refreshProductId,
                        answerIds: sourceRef.answerIds,
                        original: sourceRef.core,
                        edited
                    });
                }
                await applyRefreshIntegrationChanges(refreshProductId);
                toast.success("Product updated");
                setRefreshPhase("idle");
                setScanResult(null);
                await finishRefresh({ kind: "updated" });
                return;
            }
            setScanResult(outcome.result);
            setRefreshPhase({ verdict: outcome.verdict });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Quick compare scan failed");
            setRefreshPhase("idle");
        }
    };

    // Dialog cancel (any verdict): client-side only — close the dialog and
    // return to editing. Deliberately does NOT call any commit/write
    // function: staged mode wrote nothing pre-confirm, so there is nothing
    // to undo. This intentionally departs from the old modal's CANCEL_UPDATE
    // apply-call-on-cancel — that concept doesn't apply to a flow that never
    // wrote in the first place.
    const handleRefreshDialogCancel = () => {
        setRefreshPhase("idle");
        setScanResult(null);
    };

    const handleRefreshConfirmMinorEvolution = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        const edited = buildEditedCoreValues();
        setRefreshPhase("committing");
        try {
            await commitMinorEvolution({
                token,
                projectId: refreshProductId,
                userId,
                answerIds: sourceRef.answerIds,
                original: sourceRef.core,
                edited,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await applyRefreshIntegrationChanges(refreshProductId);
            toast.success("Product updated");
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "updated" });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to update product");
            setRefreshPhase("idle");
        }
    };

    const handleRefreshConfirmRefinementOnly = async () => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        const edited = buildEditedCoreValues();
        setRefreshPhase("committing");
        try {
            await commitRefinementOnly({
                token,
                projectId: refreshProductId,
                userId,
                answerIds: sourceRef.answerIds,
                original: sourceRef.core,
                edited,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await applyRefreshIntegrationChanges(refreshProductId);
            toast.success("Product updated");
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "updated" });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to update product");
            setRefreshPhase("idle");
        }
    };

    const handleRefreshConfirmDrasticChange = async (newProductName: string) => {
        if (!sourceRef || !token || userId == null || refreshProductId == null) return;
        setRefreshPhase("buildingDrastic");
        setBuildProgress({ phase: "" });
        try {
            const start: DrasticStartResult = await startDrasticChange({
                token,
                projectId: refreshProductId,
                userId,
                newProductName,
                comparisonResult: scanResult,
                originalAnswers: sourceRef.originalAnswers
            });
            await runDrasticBuild({
                token,
                start,
                onProgress: (phase, message) => setBuildProgress({ phase, message })
            });
            await applyRefreshIntegrationChanges(start.newProjectId);
            try {
                await dispatch(setCurrentProject(start.newProjectId)).unwrap();
            } catch {
                // Non-fatal: the new product exists; the dashboard refresh will pick it up.
            }
            setRefreshPhase("idle");
            setScanResult(null);
            await finishRefresh({ kind: "rebuilt", newProjectId: start.newProjectId });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to build new product");
            setRefreshPhase("idle");
        }
    };

    const createButton = (
        <NPButton
            tone="primary"
            disabled={!canCreate(state)}
            onClick={handleCreate}
            uiBridge={{
                id: "btn.create-product",
                label: "Create product",
                description: "Submit the Add New Product form and create the product",
                group: "new-product"
            }}
        >
            {state.isCreating ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NPIcon name="flag" size={14} color="#fff" />}
            {state.isCreating ? "Creating…" : "Create product"}
        </NPButton>
    );

    const isScanning = refreshPhase === "scanning";
    const canScan =
        refreshDirtyVsSource && state.fields.name.trim() !== "" && state.fields.problem.trim() !== "" && Object.keys(nfrErrors).length === 0 && !isScanning;
    const scanButton = (
        <NPButton tone="primary" disabled={!canScan} onClick={() => void handleRefreshScan()}>
            {isScanning ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NPIcon name="flag" size={14} color="#fff" />}
            {isScanning ? "Scanning…" : "Scan & apply changes"}
        </NPButton>
    );

    const revertEdits = async () => {
        if (!sourceRef) return;
        try {
            await cleanupRefreshStagedChanges();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to discard staged integration changes");
            return;
        }
        try {
            await discardRefreshPreview();
        } catch (error) {
            console.error("Failed to discard Context Mesh preview", error);
        }
        d({
            type: "hydrate",
            state: hydrateStateFromRefreshCore(sourceRef.core, sourceRef.users, sourceRef.team, {
                ...(sourceRef.connections ?? {}),
                files: refreshFilesConnection(sourceRef.files)
            })
        });
    };

    if (!draftLoaded || !refreshLoaded) {
        return (
            <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%", background: "var(--bg)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--fg3)" }}>
                    <CircularProgress size={20} /> {isRefresh ? "Loading product…" : "Loading product draft…"}
                </div>
            </div>
        );
    }

    if (isRefresh && refreshLoadError) {
        return (
            <div className="np-v6" style={{ display: "grid", placeItems: "center", height: "100%", background: "var(--bg)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "var(--fg3)" }}>
                    <span>Couldn't load this product for refresh.</span>
                    <NPButton tone="ghost" onClick={onBackToContextMesh ?? onCancel}>
                        {onBackToContextMesh ? "Back to Context Mesh" : "Back"}
                    </NPButton>
                </div>
            </div>
        );
    }

    const savedLabel =
        saveStatus === "saving"
            ? "Saving…"
            : saveStatus === "error"
              ? "Draft not saved"
              : lastSavedAt
                ? `Saved ${formatLocalDraftSaveTime(lastSavedAt)}`
                : "Not saved yet";

    return (
        <div className="np-v6" style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--bg)" }}>
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "16px 28px",
                    borderBottom: "1px solid var(--divider)",
                    background: "var(--bg-paper)",
                    flex: "0 0 auto"
                }}
            >
                {isRefresh && onBackToContextMesh && (
                    <NavigationBackButton
                        label="Back to Context Mesh"
                        onClick={() => requestExit(onBackToContextMesh)}
                        description="Return to the product Context Mesh"
                        bridgeId="btn.back-to-context-mesh"
                    />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.012em" }}>{isRefresh ? "Refresh Product" : "Add New Product"}</span>
                    <div style={{ fontSize: 13, color: "var(--fg3)", marginTop: 3 }}>
                        {isRefresh ? (
                            <>Editing "{sourceRef?.core.name}" — changes are staged until you confirm the scan results.</>
                        ) : (
                            <>Start with two fields. Everything below them is us working for you {NP_DASH} confirm what's right, fix what isn't.</>
                        )}
                    </div>
                </div>
            </div>

            {isRefresh && <RefreshBanner />}

            <ProgressRail steps={steps} onStepClick={scrollTo} />

            {/* Scrollable form region */}
            <div style={{ flex: 1, minHeight: 0, overflow: "hidden auto", padding: "26px 0" }}>
                <div style={{ margin: "0 auto", padding: "0 20px" }}>
                    {/* Hero row: form card + mesh card, inspector slides over */}
                    <div
                        className="np-rise"
                        style={{ display: "flex", gap: 22, alignItems: "stretch", marginBottom: 20, position: "relative" }}
                    >
                        <MeshInspector
                            open={state.meshOpen}
                            onClose={() => d({ type: "toggleMesh" })}
                            analysis={meshAnalysis}
                            updating={meshUpdating}
                        />
                        <div
                            ref={setRef("kind")}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                background: "var(--bg-paper)",
                                border: "1.5px solid rgba(99,102,241,0.4)",
                                borderRadius: 18,
                                boxShadow: "var(--shadow-2)",
                                padding: "22px 26px"
                            }}
                        >
                            {isRefresh ? (
                                <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            padding: "5px 14px",
                                            borderRadius: 999,
                                            background: "var(--np-brand)",
                                            color: "#fff"
                                        }}
                                    >
                                        {sourceRef?.core.name}
                                    </span>
                                </div>
                            ) : (
                                <KindPills
                                    kind={state.kind}
                                    scope={state.scope}
                                    locked={locked}
                                    onKind={k => d({ type: "setKind", kind: k })}
                                    onScope={sc => d({ type: "setScope", scope: sc })}
                                    onCancel={() => requestExit(onCancel)}
                                />
                            )}
                            <div style={{ display: "flex", flexDirection: "column", gap: 13, ...lockStyle }}>
                                <div ref={setRef("name")}>
                                    <ExpandingField
                                        label="Product Name"
                                        value={state.fields.name}
                                        onChange={v => d({ type: "setField", field: "name", value: v })}
                                        onClear={() => d({ type: "clearField", field: "name" })}
                                        rows={1}
                                        required
                                    />
                                </div>
                                <div ref={setRef("problem")}>
                                    <ExpandingField
                                        label="Problem Product Solves"
                                        value={state.fields.problem}
                                        onChange={v => {
                                            invalidateQuality("problem");
                                            d({ type: "setField", field: "problem", value: v });
                                        }}
                                        onClear={() => {
                                            invalidateQuality("problem");
                                            d({ type: "clearField", field: "problem" });
                                        }}
                                        onBlur={v => evaluateQuality("problem", v)}
                                        rows={2}
                                        required
                                    />
                                    <FieldQualityNote label="Problem Product Solves" quality={quality.problem} />
                                </div>
                                <div ref={setRef("solution")}>
                                    <ExpandingField
                                        label="What Solution Does The Product Offer"
                                        value={state.fields.solution}
                                        onChange={v => {
                                            cancelInference("solution");
                                            invalidateQuality("solution");
                                            d({ type: "setField", field: "solution", value: v });
                                        }}
                                        onClear={() => {
                                            cancelInference("solution");
                                            invalidateQuality("solution");
                                            d({ type: "clearField", field: "solution" });
                                        }}
                                        onBlur={v => evaluateQuality("solution", v)}
                                        placeholder={"Leave blank and we'll infer it from the problem — or describe it yourself."}
                                        rows={2}
                                        badge={<InferButton onInfer={() => runInfer("solution")} disabled={!canInfer} busy={inferringFields.has("solution")} />}
                                    />
                                    <FieldQualityNote label="What Solution Does The Product Offer" quality={quality.solution} />
                                </div>
                                {!isRefresh && (
                                    <div ref={setRef("formFactor")}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fg2)", letterSpacing: "0.03em" }}>
                                                Primary Form Factor
                                            </span>
                                            <RequiredBadge />
                                        </div>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={state.primaryFormFactor}
                                            // Required: the reducer ignores the null MUI sends when the
                                            // active pill is clicked again, so a choice is always set.
                                            onChange={(_, value: PrimaryFormFactor | null) => d({ type: "setPrimaryFormFactor", primaryFormFactor: value })}
                                            sx={{ gap: "10px", "& .MuiToggleButtonGroup-grouped": { margin: 0 } }}
                                        >
                                            {FORM_FACTOR_OPTIONS.map(option => (
                                                <ToggleButton key={option.value} disableRipple value={option.value} sx={FORM_FACTOR_PILL_SX}>
                                                    {option.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </div>
                                )}
                                <div
                                    ref={setRef("nfr")}
                                    data-testid="nfr-section"
                                    style={{
                                        marginTop: 3,
                                        paddingTop: 14,
                                        borderTop: "1px solid var(--divider)"
                                    }}
                                >
                                    <button
                                        type="button"
                                        aria-expanded={nfrOpen}
                                        aria-label={isRefresh ? (nfrOpen ? "Hide optional NFRs" : "Add or edit optional NFRs") : "Non-Functional Requirements"}
                                        onClick={() => setNfrOpen(open => !open)}
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 9,
                                            padding: 0,
                                            border: 0,
                                            background: "transparent",
                                            color: "inherit",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            fontFamily: "inherit"
                                        }}
                                    >
                                        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--fg2)" }}>
                                            {isRefresh ? (nfrOpen ? "Hide optional NFRs" : "Add or edit optional NFRs") : "Non-Functional Requirements"}
                                        </span>
                                        <OptionalBadge>Optional — improves technical user stories</OptionalBadge>
                                        <span
                                            aria-hidden
                                            style={{
                                                marginLeft: "auto",
                                                color: "var(--fg4)",
                                                fontSize: 17,
                                                lineHeight: 1,
                                                transform: nfrOpen ? "rotate(180deg)" : "none",
                                                transition: "transform .15s ease"
                                            }}
                                        >
                                            ▾
                                        </span>
                                    </button>
                                    <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--fg3)" }}>
                                        Add technical constraints now to make them available to downstream story generation.
                                    </div>
                                    {nfrOpen && (
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                                                gap: "15px 18px",
                                                marginTop: 16
                                            }}
                                        >
                                            {NFR_FIELDS.map(field => (
                                                <ExpandingField
                                                    key={field.key}
                                                    label={field.label}
                                                    value={state.nfr[field.key]}
                                                    onChange={value => {
                                                        cancelInference(field.inferField);
                                                        d({ type: "setNfrField", field: field.key, value });
                                                    }}
                                                    onClear={() => {
                                                        cancelInference(field.inferField);
                                                        d({ type: "clearNfrField", field: field.key });
                                                    }}
                                                    placeholder={field.placeholder}
                                                    rows={2}
                                                    maxLength={field.maxLength}
                                                    error={nfrErrors[field.key]}
                                                    badge={
                                                        <InferButton
                                                            onInfer={() => runInfer(field.inferField)}
                                                            disabled={!canInfer}
                                                            busy={inferringFields.has(field.inferField)}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ flex: "0 0 240px", ...lockStyle }}>
                            <MeshCard
                                analysis={meshAnalysis}
                                status={meshStatus}
                                updating={meshUpdating}
                                stale={meshStale}
                                error={meshError}
                                open={state.meshOpen}
                                onToggle={() => d({ type: "toggleMesh" })}
                                onRetry={retryMesh}
                            />
                        </div>
                    </div>

                    {/* Drafted cards row */}
                    <div
                        className="np-rise"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                            marginBottom: 22,
                            alignItems: "stretch",
                            ...lockStyle
                        }}
                    >
                        <div ref={setRef("users")} style={{ display: "flex", flexDirection: "column" }}>
                            <ListEditorCard
                                icon="users"
                                title="Target Users"
                                items={state.users}
                                addLabel="Add target user"
                                placeholder="e.g., Law firms"
                                badge={<InferButton onInfer={() => runInfer("users")} disabled={!canInfer} busy={inferringFields.has("users")} />}
                                onChangeItem={(i, v) => {
                                    invalidateQuality("personas");
                                    d({ type: "setListItem", list: "users", index: i, value: v });
                                }}
                                onRemoveItem={i => {
                                    invalidateQuality("personas");
                                    d({ type: "removeListItem", list: "users", index: i });
                                }}
                                onAddItem={() => {
                                    invalidateQuality("personas");
                                    d({ type: "addListItem", list: "users" });
                                }}
                                onClear={() => {
                                    invalidateQuality("personas");
                                    d({ type: "clearList", list: "users" });
                                }}
                                onBlur={evaluatePersonasQuality}
                            />
                            <FieldQualityNote label="Target Users" quality={quality.personas} />
                        </div>
                        <div ref={setRef("team")} style={{ display: "flex", flexDirection: "column" }}>
                            <ListEditorCard
                                icon="team"
                                title="Team Members"
                                items={state.team}
                                addLabel="Add team member"
                                placeholder="e.g., Jordan Lee — Product Manager"
                                // Team (Q25) is never part of the refresh scan payload; edits
                                // persist through writeCoreUpdates' direct-write path (the same
                                // one that handles renames), so the card is editable in both modes.
                                badge={<OptionalBadge>{"Optional — Highly Suggested"}</OptionalBadge>}
                                onChangeItem={(i, v) => d({ type: "setListItem", list: "team", index: i, value: v })}
                                onRemoveItem={i => d({ type: "removeListItem", list: "team", index: i })}
                                onAddItem={() => d({ type: "addListItem", list: "team" })}
                                onClear={() => d({ type: "clearList", list: "team" })}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Integrations — pinned above the footer */}
            <div ref={setRef("integrations")} style={{ ...lockStyle }}>
                <IntegrationsBar connections={state.connections} onOpenSource={setOpenSource} blockedSources={trackerBlocked} />
            </div>

            {openSource && openSource !== "files" && token && userId != null && (
                <ConnectSourceModal
                    source={openSource as ConnectSourceId}
                    token={token}
                    userId={userId}
                    existing={state.connections[openSource]}
                    onComplete={connection => handleSourceComplete(openSource, connection)}
                    onDisconnect={() => {
                        handleSourceDisconnect(openSource);
                        setOpenSource(null);
                    }}
                    onClose={() => setOpenSource(null)}
                    onAborted={handleSourceAborted}
                />
            )}

            {openSource === "files" && token && isRefresh && refreshProductId != null && sourceRef && (
                <RefreshFilesModal
                    files={sourceRef.files}
                    token={token}
                    projectId={refreshProductId}
                    onFilesUpdated={handleRefreshFilesUpdated}
                    onChanged={handleRefreshFilesChanged}
                    onClose={() => setOpenSource(null)}
                />
            )}

            {openSource === "files" && token && !isRefresh && (
                <FilesModal
                    existing={state.connections.files}
                    token={token}
                    onFileAdded={handleFilesUploaded}
                    onFileRemoved={handleFileRemoved}
                    onClose={() => setOpenSource(null)}
                    intakeFiles={intakeFiles}
                    onIntakeConsumed={onIntakeConsumed}
                />
            )}

            {isRefresh && (
                <>
                    <ScanProgressOverlay open={refreshPhase === "scanning"} phase={buildProgress.phase} message={buildProgress.message} />
                    <NoChangesDialog open={typeof refreshPhase === "object" && refreshPhase.verdict === "NO_CHANGES"} onClose={handleRefreshDialogCancel} />
                    <RefinementOnlyDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "REFINEMENT_ONLY"}
                        result={scanResult}
                        onConfirm={() => void handleRefreshConfirmRefinementOnly()}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <MinorEvolutionDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "MINOR_EVOLUTION"}
                        result={scanResult}
                        onConfirm={() => void handleRefreshConfirmMinorEvolution()}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <DrasticChangeDialog
                        open={typeof refreshPhase === "object" && refreshPhase.verdict === "DRASTIC_CHANGE"}
                        onConfirm={name => void handleRefreshConfirmDrasticChange(name)}
                        onCancel={handleRefreshDialogCancel}
                    />
                    <BuildProgressOverlay open={refreshPhase === "buildingDrastic"} phase={buildProgress.phase} message={buildProgress.message} />
                </>
            )}

            {pendingConfirm === "startOver" && (
                <ConfirmDialog
                    open
                    title="Start over?"
                    message="Discard this product draft and start over? This cannot be undone."
                    confirmLabel="Start over"
                    cancelLabel="Keep editing"
                    destructive
                    onConfirm={() => {
                        setPendingConfirm(null);
                        void performDiscardDraft();
                    }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}

            {pendingConfirm === "saveDraft" && (
                <ConfirmDialog
                    open
                    title="Draft saved"
                    message="Do you wish to continue working on this draft?"
                    confirmLabel="Yes, keep working"
                    cancelLabel="No, back to Product Hub"
                    onConfirm={() => setPendingConfirm(null)}
                    onCancel={() => {
                        setPendingConfirm(null);
                        onCancel();
                    }}
                    onClose={() => setPendingConfirm(null)}
                />
            )}

            {pendingConfirm === "cancel" && (
                <ConfirmDialog
                    open
                    title="Discard unsaved changes?"
                    message="Any unsaved changes will be lost. Are you sure?"
                    confirmLabel="Yes, leave"
                    cancelLabel="Keep working"
                    destructive
                    onConfirm={() => {
                        setPendingConfirm(null);
                        const exitAction = pendingExitActionRef.current ?? onCancel;
                        pendingExitActionRef.current = null;
                        if (isRefresh) {
                            void cleanupRefreshStagedChanges()
                                .catch(error => {
                                    console.error("Failed to clean up staged refresh artifacts", error);
                                    toast.warning("Some staged integration data could not be cleaned up.");
                                })
                                .then(() => discardRefreshPreview())
                                .catch(error => {
                                    console.error("Failed to discard Context Mesh preview", error);
                                })
                                .finally(exitAction);
                        } else {
                            exitAction();
                        }
                    }}
                    onCancel={() => {
                        pendingExitActionRef.current = null;
                        setPendingConfirm(null);
                    }}
                />
            )}

            {/* Footer */}
            <div style={{ ...lockStyle }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "14px 26px",
                        borderTop: "1px solid var(--divider)",
                        background: "var(--bg-paper)",
                        flex: "0 0 auto"
                    }}
                >
                    {isRefresh ? (
                        <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>
                            Text and integration changes apply after the scan; file changes apply immediately.
                        </span>
                    ) : (
                        <>
                            <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>
                                Create now, enrich later {NP_DASH} the mesh keeps growing after the product exists.
                            </span>
                            <span style={{ fontSize: 12, color: saveStatus === "error" ? "#b42318" : "var(--fg3)" }}>{savedLabel}</span>
                        </>
                    )}
                    <span style={{ flex: 1 }} />
                    {isRefresh ? (
                        <NPButton tone="ghost" onClick={() => void revertEdits()} disabled={!refreshDirtyVsSource}>
                            Revert edits
                        </NPButton>
                    ) : (
                        <>
                            {dirty && (
                                <NPButton tone="ghost" onClick={() => setPendingConfirm("startOver")} disabled={saveStatus === "saving"}>
                                    Start over
                                </NPButton>
                            )}
                            <NPButton tone="ghost" onClick={handleSaveDraft} disabled={!dirty || saveStatus === "saving" || !hasUnsavedChanges}>
                                Save draft
                            </NPButton>
                        </>
                    )}
                    <NPButton tone="ghost" onClick={() => requestExit(onCancel)}>
                        Cancel
                    </NPButton>
                    {isRefresh ? scanButton : createButton}
                </div>
            </div>
        </div>
    );
});
